class CartNotification extends HTMLElement {
  constructor() {
    super();
    this.notification = this.querySelector('#cart-notification');
    this.header = document.querySelector('sticky-header');
    this.activeElement = null;

    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.querySelectorAll('[data-cart-drawer-close]').forEach((button) => button.addEventListener('click', () => this.close()));
    this.addEventListener('click', (event) => this.handleClick(event));
  }

  async handleClick(event) {
    const recommendationButton = event.target.closest('[data-cart-recommendation-add]');
    if (recommendationButton) {
      await this.addRecommendation(recommendationButton);
      return;
    }

    const quantityButton = event.target.closest('.qty-btn');
    if (!quantityButton) return;
    const wrapper = quantityButton.closest('.cart-notification-qty');
    const input = wrapper?.querySelector('.qty-input');
    if (!wrapper?.dataset.lineKey || !input) return;

    const delta = quantityButton.classList.contains('qty-plus') ? 1 : -1;
    const quantity = Math.max(0, Number(input.value || 1) + delta);
    quantityButton.disabled = true;

    try {
      await fetch(this.dataset.cartChangeUrl || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: wrapper.dataset.lineKey, quantity })
      });
      await this.refreshAndOpen();
    } finally {
      quantityButton.disabled = false;
    }
  }

  async addRecommendation(button) {
    if (button.disabled) return;
    button.disabled = true;
    button.classList.add('loading');

    try {
      const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] })
      });
      if (!response.ok) throw new Error('Cart add failed');
      await this.refreshAndOpen();
    } catch (error) {
      console.error(error);
      button.disabled = false;
    } finally {
      button.classList.remove('loading');
    }
  }

  open() {
    this.classList.add('is-open');
    document.body.classList.add('cart-drawer-open');
    this.notification?.focus();
    if (this.notification && typeof trapFocus === 'function') trapFocus(this.notification);
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const container = document.getElementById(section.id);
      const sectionHtml = parsedState.sections?.[section.id];
      if (!container || !sectionHtml) return;
      const inner = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (inner !== null) container.innerHTML = inner;
    });

    if (this.header) this.header.reveal();
    this.refreshCartState().then(() => this.open());
  }

  async refreshAndOpen() {
    const sectionIds = this.getSectionsToRender().map((section) => section.id);
    const url = `${this.dataset.cartUrl || '/cart'}?sections=${encodeURIComponent(sectionIds.join(','))}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    const sections = await response.json();

    this.getSectionsToRender().forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container || !sections[section.id]) return;
      const inner = this.getSectionInnerHTML(sections[section.id], section.selector);
      if (inner !== null) container.innerHTML = inner;
    });

    await this.refreshCartState();
    this.open();
  }

  async refreshCartState() {
    const response = await fetch(this.dataset.cartJsonUrl || '/cart.js', { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    const threshold = Number(this.dataset.shippingThreshold || 5000);
    const remaining = Math.max(0, threshold - Number(cart.total_price || 0));
    const progress = threshold > 0 ? Math.min(100, (Number(cart.total_price || 0) / threshold) * 100) : 100;

    const message = this.querySelector('[data-cart-shipping-message]');
    if (message) {
      message.textContent = remaining > 0
        ? this.dataset.shippingLeft.replace('__AMOUNT__', this.formatMoney(remaining))
        : this.dataset.shippingFree;
    }

    const progressBar = this.querySelector('[data-cart-progress-fill]');
    if (progressBar) progressBar.style.width = `${progress}%`;

    this.updateCartIconBubbleCount(cart.item_count);
    this.updateCartNotificationButtonCount(cart.item_count);
    this.filterRecommendations(cart.items || []);
  }

  filterRecommendations(items) {
    const productIds = new Set(items.map((item) => String(item.product_id)));
    const cards = Array.from(this.querySelectorAll('[data-cart-recommendation]'));
    let visible = 0;

    cards.forEach((card) => {
      const show = !productIds.has(String(card.dataset.productId)) && visible < 3;
      card.hidden = !show;
      if (show) visible += 1;
    });

    const wrapper = this.querySelector('[data-cart-recommendations]');
    if (wrapper) wrapper.hidden = visible === 0;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'lt-LT', {
      style: 'currency',
      currency: this.dataset.currency || 'EUR'
    }).format(Number(cents || 0) / 100);
  }

  getSectionsToRender() {
    return [
      { id: 'cart-notification-product', selector: '#cart-notification-products' },
      { id: 'cart-notification-button' },
      { id: 'cart-icon-bubble' }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const selected = documentFragment.querySelector(selector);
    return selected ? selected.innerHTML : null;
  }

  updateCartIconBubbleCount(itemCount) {
    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink) return;
    const countBubble = cartLink.querySelector('.cart-count-bubble');
    if (!countBubble) return;
    countBubble.classList.toggle('hidden', Number(itemCount) === 0);
    const visible = countBubble.querySelector('[aria-hidden="true"]');
    const hidden = countBubble.querySelector('.visually-hidden');
    if (visible) visible.textContent = itemCount;
    if (hidden) hidden.textContent = itemCount;
  }

  updateCartNotificationButtonCount(itemCount) {
    const button = document.getElementById('cart-notification-button');
    if (button) button.textContent = this.dataset.viewCart.replace('__COUNT__', itemCount);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-notification')) customElements.define('cart-notification', CartNotification);
